import L from 'leaflet'
import { useEffect, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'

import { PlanningUnassignedGridRow } from '@/entities/gridTables/model/planning/PlanningUnassignedGridRow'
import { PlanningUnassignedTable } from '@/entities/gridTables/model/planning/PlanningUnassignedTable'
import { RunsheetUnassignedGridRow } from '@/entities/gridTables/model/runsheet/RunsheetUnassignedGridRow'
import { RunsheetUnassignedTable } from '@/entities/gridTables/model/runsheet/RunsheetUnassignedTable'
import { selectBusinessModel } from '@/entities/user'
import { selectUomVolume } from '@/entities/user/selectors/selectUomVolume'
import { selectUomWeight } from '@/entities/user/selectors/selectUomWeight'
import { LocationMarkerIcon } from '@/shared/components/MapIcons/LocationMarkerIcon'
import { useAppSelector } from '@/shared/store'
import { useTheme } from '@mui/material'

export type useDrawUnassignedOrdersArgs = {
  map: L.Map | null
  unassignedTables: RunsheetUnassignedTable[] | PlanningUnassignedTable[]
}

/**
 * The following hook is used to draw the orders on the map widget.
 * 
 * @param map the Leaflet map object
 * @param unassignedTables the unassigned order tables to draw
 * @returns void
 */
export const useDrawUnassignedOrders = ({
  map,
  unassignedTables,
}: useDrawUnassignedOrdersArgs) => {
  const theme = useTheme()
  const uomWeight = useAppSelector(state => selectUomWeight(state))
  const uomVolume = useAppSelector(state => selectUomVolume(state))
  const businessModel = useAppSelector(selectBusinessModel)
  const orderMarkersRef = useRef<L.LayerGroup | null>(null)

  const createLocationIcon = (color: string, content: number | string) => {
    const svgString = ReactDOMServer.renderToStaticMarkup(
      <LocationMarkerIcon
        fillColor={color ?? '#000'}
        content={content}
        className="depot-icon-svg"
      />,
    )

    return L.divIcon({
      html: svgString,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
    })
  }

  const createLocationTooltip = (
    sameLocationOrderRows: (PlanningUnassignedGridRow | RunsheetUnassignedGridRow)[],
    scheduleName: string,
  ) => {
    let tooltip = ''

    if (sameLocationOrderRows.length === 0) {
      return tooltip
    }

    tooltip += `<p style='margin: 2px 0'><strong>Assigned To:</strong> ${scheduleName}</p>
      <hr/>`

    // Iterate through the orders and create a tooltip for each order
    for (let i = 0; i < sameLocationOrderRows.length; i++) {
      const ord = sameLocationOrderRows[i]

      tooltip += `
        <p style='margin: 2px 0'><strong>Order No.:</strong> ${ord.orderNumber}</p>
        <p style='margin: 2px 0'><strong>Weight:</strong> ${ord.weight} ${uomWeight}</p>
        <p style='margin: 2px 0'><strong>Volume:</strong> ${ord.volume} ${uomVolume}</p>
        <p style='margin: 2px 0'><strong>Quantity:</strong> ${ord.orderQuantity}</p>
        <hr/>
        <strong>Depot Details</strong>
        <p style='margin: 2px 0'><strong>Location Name:</strong> ${ord.depotName}</p>
        <p style='margin: 2px 0'><strong>Address:</strong> ${ord.depotDisplayAddress}</p>
        <hr/>
        <strong>Customer Details</strong>
        <p style='margin: 2px 0'><strong>Location Name:</strong> ${ord.customerName}</p>
        <p style='margin: 2px 0'><strong>Address:</strong> ${ord.customerAddress}</p>`


      if (i < sameLocationOrderRows.length - 1) {
        tooltip += '<hr/>'
      }
    }

    return tooltip
  }

  // Plot orders on the map
  useEffect(() => {
    if (!map || unassignedTables?.length === 0) return

    if (orderMarkersRef.current) {
      orderMarkersRef.current.clearLayers()
    } else {
      orderMarkersRef.current = L.layerGroup().addTo(map)
    }

    // We render the orders based on the unassigned order tables
    unassignedTables.forEach((unassignedTable) => {
      const unassignedTableRows = unassignedTable?.rows

      if (!unassignedTableRows) return

      // Pre-compute the stops that are in the same location
      const coordinatesToRowsLookup = new Map<string, (PlanningUnassignedGridRow | RunsheetUnassignedGridRow)[]>()

      for (let i = 0; i < unassignedTableRows.length; i++) {
        const row = unassignedTableRows[i]

        const key = `${row.customerLatitude},${row.customerLongitude}`

        if (!coordinatesToRowsLookup.has(key)) {
          coordinatesToRowsLookup.set(key, [])
        }

        coordinatesToRowsLookup.get(key)!.push(row)
      }

      // Iterate through the rows and plot the markers
      for (let i = 0; i < unassignedTableRows.length; i++) {
        const row = unassignedTableRows[i]

        // Plot marker
        if (!row.customerLatitude || !row.customerLongitude) continue

        const key = `${row.customerLatitude},${row.customerLongitude}`
        const ordersWithSameLocation = coordinatesToRowsLookup.get(key) ?? []

        if (ordersWithSameLocation.length === 0) {
          continue
        }

        const orderMarkerIcon = createLocationIcon(theme.palette.primary.main, 'U')
        const marker = L.marker([row.customerLatitude, row.customerLongitude], { icon: orderMarkerIcon })
        const tooltip = createLocationTooltip(
          ordersWithSameLocation,
          'scenarioName' in unassignedTable ? unassignedTable.scenarioName : unassignedTable.runsheetName,
        )

        marker.bindTooltip(tooltip,
          {
            permanent: false,
            direction: 'right',
            offset: L.point(15, -25),
          },
        )
        orderMarkersRef.current!.addLayer(marker)
      }
    })

    return () => {
      // Need to clear the markers when the component unmounts
      //   so it can be re-added to the map when the component mounts again
      if (orderMarkersRef.current) {
        orderMarkersRef.current.clearLayers()
        orderMarkersRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, businessModel, unassignedTables, theme.palette.primary.main])
}